import type { CalEventType, CalEventTypeHost, CalTeamClient } from "@/lib/cal/client";
import {
  getManagedHostCalUserId,
  isManagedSchedulingType,
} from "./sync-practice-appointment-type-child-event-mappings";

/**
 * Provisions Cal.com managed child event types for a team member who was added AFTER a practice's
 * appointment types were created.
 *
 * Why this exists: Cal only creates managed child events for the members present when a parent
 * event type is created (`assignAllTeamMembers: true` at create time). It does NOT retroactively
 * provision children for members who join later, so every booking for such a host 404s
 * ("Event type with slug … belonging to user … not found") until Cal is nudged to re-run
 * assignment. Re-PATCHing `assignAllTeamMembers: true` makes Cal reconcile the managed children
 * against the CURRENT roster and create the missing child — see ENG-5216.
 *
 * Safety: only parents that are ALREADY `assignAllTeamMembers: true` are touched. Parents with an
 * intentional custom host subset (`assignAllTeamMembers: false` + explicit `hosts`) are left alone,
 * because flipping them to assign-all would clobber that deliberate scoping (ENG-3412). Parents
 * where the host is already assigned are skipped, so a normal provider-add is a no-op.
 */

export interface ProvisionManagedChildEventsResult {
  scannedParents: number;
  provisionedCount: number;
  alreadyAssignedCount: number;
  skippedNonManagedOrScopedCount: number;
  failedCount: number;
}

interface ProvisionManagedChildEventsInput {
  calTeamClient: CalTeamClient;
  teamId: number;
  calUserId: number;
  logger?: {
    info: (message: string, metadata?: Record<string, unknown>) => void;
    warn: (message: string, metadata?: Record<string, unknown>) => void;
  };
}

/** Resolve a host's Cal user id; `hosts` can carry bare id strings, which have no user id. */
function hostCalUserId(host: CalEventTypeHost | string): number | undefined {
  return typeof host === "string" ? undefined : getManagedHostCalUserId(host);
}

/** A top-level MANAGED parent, not a child row. Shares the sibling's managed-scheduling check. */
function isManagedParent(eventType: CalEventType): boolean {
  return eventType.parentEventTypeId == null && isManagedSchedulingType(eventType.schedulingType);
}

export async function provisionManagedChildEventsForHost({
  calTeamClient,
  teamId,
  calUserId,
  logger,
}: ProvisionManagedChildEventsInput): Promise<ProvisionManagedChildEventsResult> {
  const response = await calTeamClient.getTeamEventTypes({ teamId, hostsLimit: 500 });
  // Cal can answer 2xx with an error envelope (status: "error") and empty data. Treat that as a
  // failure and throw so this step's retries run — an empty parent list would otherwise look like
  // "nothing to provision" and silently skip a genuinely-broken host. The job wraps this in a
  // try/catch, so a terminal failure still downgrades to a warn without blocking the mirror sync.
  if (response.status !== "success") {
    throw new Error(`getTeamEventTypes returned status "${response.status}" for team ${teamId}`);
  }
  const eventTypes = response.data ?? [];

  // Cal's team-list endpoint does not guarantee per-event metadata (its own client docs flag
  // schedulingType/host fields as "not guaranteed" there). If rows arrive without schedulingType,
  // managed-parent detection silently under-counts and we could provision nothing while still
  // reporting success — indistinguishable from a team with genuinely zero managed parents. Surface
  // it so a silent 0 is observable rather than hidden (ENG-5216).
  const missingSchedulingTypeCount = eventTypes.filter((et) => et.schedulingType == null).length;
  if (missingSchedulingTypeCount > 0) {
    logger?.warn(
      "getTeamEventTypes returned event types without schedulingType; managed-parent detection may under-count",
      { teamId, calUserId, missingSchedulingTypeCount, totalCount: eventTypes.length },
    );
  }

  const parents = eventTypes.filter(isManagedParent);

  const result: ProvisionManagedChildEventsResult = {
    scannedParents: parents.length,
    provisionedCount: 0,
    alreadyAssignedCount: 0,
    // Rows excluded by isManagedParent (non-managed types + child rows). Scoped assign-all-false
    // managed parents are added to this same bucket inside the loop below.
    skippedNonManagedOrScopedCount: eventTypes.length - parents.length,
    failedCount: 0,
  };

  for (const parent of parents) {
    // Never touch a parent with a deliberate custom host subset — re-asserting assign-all there
    // would silently reassign it to the whole team (ENG-3412).
    if (parent.assignAllTeamMembers !== true) {
      result.skippedNonManagedOrScopedCount += 1;
      continue;
    }

    // hosts membership is a reliable proxy for "child exists": Cal does NOT list a team member in
    // an assign-all parent's `hosts` until it has actually provisioned that member's managed child.
    // Verified in prod across FocusMD/Shona Martin/Immortals (ENG-5216) — a member present on the
    // team for days was still absent from every parent's hosts while their children were missing,
    // and appeared only after provisioning. So skip = already provisioned; the PATCH is also
    // idempotent, so a rare false-negative here would only cost a redundant no-op write.
    const hostIds = (parent.hosts ?? [])
      .map(hostCalUserId)
      .filter((id): id is number => id != null);
    if (hostIds.includes(calUserId)) {
      result.alreadyAssignedCount += 1;
      continue;
    }

    try {
      // Re-assert assign-all so Cal reconciles managed children against the current roster and
      // creates the missing child for this host. Idempotent: Cal only creates what's absent.
      await calTeamClient.updateTeamEventType({
        teamId,
        eventTypeId: parent.id,
        request: { assignAllTeamMembers: true },
      });
      result.provisionedCount += 1;
    } catch (error) {
      result.failedCount += 1;
      logger?.warn("Failed to provision managed child event for host", {
        teamId,
        calUserId,
        eventTypeId: parent.id,
        eventTypeSlug: parent.slug,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  // Per-parent PATCH failures are reported via result.failedCount (the loop above continues past
  // each so every parent is still attempted). We deliberately do NOT throw on failedCount here:
  // this runs inside a single step.run whose failure would re-invoke the whole handler and block
  // the downstream mirror sync for the entire Inngest retry window — violating the handler's
  // "provisioning must not block the mirror sync" invariant. Instead the caller alerts on
  // failedCount > 0 so a partial failure is visible rather than silently swallowed, and proper
  // non-blocking retry is tracked in ENG-5240. (A total getTeamEventTypes failure above still
  // throws — there's nothing to mirror if the catalog fetch itself failed.)
  logger?.info("Provisioned managed child events for host", { teamId, calUserId, ...result });
  return result;
}
